import _ from 'lodash'
import restApi from '../api'
import { actions as csrfActions } from 'redux-csrf'
import { dispatchWithException } from './baseUtils'
import { setGAUserDimensions } from '../utils/googleAnalyticsUtils'
import { sessionExpire, setResources, setUser, setActiveGroup } from '../store/userSession'
import { setClinicianId, setSelectedLCU } from '../store/membersView'
import { clear } from '../store/rootReducer'
import { populateEpicPatient, populateEpicPatientId, setEpicLoading } from '../store/epic'
import { type IEpicPatient } from '../interfaces/store/epicStore.interface'

/* ********************************************************************************
 * Endpoints for logging in/out.
 * ********************************************************************************/
const authEndpoints = {
  csrf: {
    url: '/csrf',
    options: {
      method: 'get',
      headers: {
        'X-Web-App': true
      }
    },
    transformer (data) {
      return { csrfToken: data }
    },

    postfetch: [({ data, actions, dispatch }) => {
      dispatchWithException(csrfActions.setCsrfToken(data.csrfToken), dispatch)
      dispatchWithException(actions.session(), dispatch)
    }]
  },

  sendAnalytics: {
    url: 'users/:id/analytics',
    options: {
      method: 'post'
    }
  },

  setActiveGroup: {
    url: '/auth/setActiveGroup',
    options: {
      method: 'post'
    },

    postfetch: [
      ({ data, actions, dispatch }) => {
        dispatchWithException(setActiveGroup(data.activeGroup), dispatch)
        dispatchWithException(actions.resources(), dispatch)
        dispatchWithException(setSelectedLCU('All'), dispatch)
      }
    ]
  },

  session: {
    url: '/auth',
    options: {
      method: 'get'
    },

    postfetch: [
      ({ data, actions, dispatch }) => {
        setUserData(data, actions, dispatch, data.logged_in)
      }
    ]
  },

  sso: {
    url: '/epic/launch',
    options: {
      method: 'get'
    },
    postfetch: [
      ({ data, dispatch }) => {
        dispatchWithException(setEpicLoading(false), dispatch)
        if (_.isEmpty(data) || !data.logged_in) return

        dispatchWithException(setUser({ ...data.user, allowedGroups: data.allowedGroups }), dispatch)
        dispatchWithException(setClinicianId(data.user.id), dispatch)
        dispatchWithException(setActiveGroup(null), dispatch)
        setGAUserDimensions(data.user)

        if (data.ejentaMemberId) dispatchWithException(populateEpicPatientId(data.ejentaMemberId as string), dispatch)
        else if (data.newPatient) {
          dispatchWithException(setActiveGroup(data.allowedGroups?.[0] ?? data.activeGroup ?? data.group), dispatch)
          dispatchWithException(populateEpicPatient(data.newPatient as IEpicPatient), dispatch)
        }
      }
    ]
  },

  login: {
    url: '/auth/login',
    options: {
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    },

    postfetch: [
      ({ data, actions, dispatch, request, getState }) => {
        if (!_.isEmpty(data.user)) {
          request.pathvars.promise.resolve()
          setUserData(data, actions, dispatch, true)
        } else {
          let failedLoginMessage = 'Incorrect username and password combination.'

          if (getState().userSession.loggingInWithPhone) {
            failedLoginMessage = 'Incorrect phone and code combination. Please try again.'
          }

          request.pathvars.promise.reject(failedLoginMessage)
        }
      }
    ]
  },

  logout: {
    url: '/auth/logout',
    options: {
      method: 'get'
    },

    postfetch: [
      // Redirect done in Router, not here
      ({ dispatch, getState }) => {
        const showSessionExpireMsg = getState().userSession.showSessionExpireMessage
        dispatchWithException(clear(), dispatch)
        if (showSessionExpireMsg) {
          dispatchWithException(sessionExpire(), dispatch)
        }
        dispatch(restApi.actions.csrf())
      }
    ]
  },

  resources: {
    url: '/resources',
    options: {
      method: 'get'
    },

    postfetch: [({ data, dispatch }) => {
      dispatchWithException(setResources(data), dispatch)
    }]
  },

  // Link user's account to Amazon
  loginAmazon: {
    url: '/auth/link/amazon',
    options: {
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    },

    postfetch: [({ data, request }) => {
      if (_.isEmpty(data) || !data.logged_in) {
        request.pathvars.promise.reject('Incorrect username and password combination.')
        return
      }

      // Redirect to whatever URL the InteractionService sends back
      window.location = data.redirect_url
    }]
  }
}

const setUserData = (data: any, actions: any, dispatch: any, isLoggedIn: boolean): void => {
  dispatchWithException(setUser({ ...data.user, allowedGroups: data.allowedGroups }), dispatch)
  dispatchWithException(setClinicianId(data.user.id), dispatch)
  if (data.user) setGAUserDimensions(data.user) // Send user data to Google Analytics

  if (data?.activeGroup) {
    dispatchWithException(setActiveGroup(data.activeGroup), dispatch)
    dispatchWithException(actions.resources(), dispatch)
  } else {
    dispatchWithException(setActiveGroup(null), dispatch)

    // if this is a single group user, and no active group is selected, call for resources
    if (!data?.allowedGroups?.length && isLoggedIn) {
      dispatchWithException(actions.resources(), dispatch)
    }
  }
}

export default authEndpoints
